import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient } from '../utils/apiUtils';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import './ReportModule.css';

const EvaluationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    feedback: '',
    decision: ''
  });

  useEffect(() => {
    // Fetch data first, then check permissions based on ownership
    fetchEvaluationData();
  }, [id]);

  const fetchEvaluationData = async () => {
    try {
      setLoading(true);
      
      // Fetch evaluation data
      const evaluationResponse = await apiClient.get(`/evaluations/${id}`);
      const evaluationData = evaluationResponse.data;
      setEvaluation(evaluationData);

      // Check permissions - only owner (evaluator) or Admin can edit
      if (user && evaluationData.evaluator_id !== user.user_id && user.role !== 'Admin') {
        navigate('/reports');
        return;
      }

      // Fetch project data
      const projectResponse = await apiClient.get(`/projects/${evaluationData.project_id}`);
      setProject(projectResponse.data);

      // Set form data
      setFormData({
        feedback: evaluationData.feedback || '',
        decision: evaluationData.decision || ''
      });

    } catch (err) {
      console.error('Error fetching evaluation data:', err);
      setError('Failed to load evaluation data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.feedback.trim() || !formData.decision) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await apiClient.put(`/evaluations/${id}`, {
        feedback: formData.feedback,
        decision: formData.decision
      });

      setSuccess('Evaluation updated successfully!');
      
      // Redirect to evaluation view after a short delay
      setTimeout(() => {
        navigate(`/reports/${id}`);
      }, 1500);

    } catch (err) {
      console.error('Error updating evaluation:', err);
      setError(err.response?.data?.error || 'Failed to update evaluation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading evaluation data...</p>
        </div>
      </Container>
    );
  }

  if (error && !evaluation) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-danger" onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Edit Evaluation</h2>
          <p className="text-muted mb-0">Project: {project?.title}</p>
        </div>
        <Button variant="outline-secondary" onClick={() => navigate(`/reports/${id}`)}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to Report
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-pencil me-2"></i>
            Edit Evaluation Details
          </h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Decision *</Form.Label>
                  <Form.Select
                    name="decision"
                    value={formData.decision}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a decision</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Needs Revision">Needs Revision</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Evaluator</Form.Label>
                  <Form.Control
                    type="text"
                    value={evaluation?.evaluator_name || ''}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Feedback *</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="feedback"
                value={formData.feedback}
                onChange={handleChange}
                placeholder="Enter your evaluation feedback..."
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button 
                type="button" 
                variant="outline-secondary" 
                onClick={() => navigate(`/reports/${id}`)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EvaluationEdit;
